// NewsTalk (c) Copyright 2023 by Qwixotic
// 
// File:   Section.js
// Author: Percipient Reverence
// Date:   1-May-2023
//
// Description:
//
//   Display the articles of a section in order of their most recent replies, just like a forum in
//   any normal discussion board.  If an article has no replies then use the date of the article.

// Javascript imports
import dayjs from 'dayjs'

// React imports
import React, {useEffect, useMemo, useState} from 'react'
import {useParams} from 'react-router-dom'

// MUI imports
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import IconButton from '@mui/material/IconButton'
import NewspaperIcon from '@mui/icons-material/Newspaper';
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import Tooltip from '@mui/material/Tooltip'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { blue } from '@mui/material/colors'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { DataGrid, GridRowsProp, GridColDef, useGridApiRef } from '@mui/x-data-grid'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

// Local imports
import { ArticleItem, FormattedDateTime, Link, MediaOutletImageLink, OffGridCommentLink, PageListPagination,
         PageListPaginationRightAlign, TableListItem } from './lib/Components'
import { ConfigVars, myFetch } from './lib/Util'

export const SectionEP = () => {

  console.log('Rendering SectionEP')

  // Prepare to access DataGrid from Buttons
  const apiRef = useGridApiRef()
  
  const Params = useParams()

  // Fetch the section ID
  let SID = Params.SID ? parseInt(Params.SID) : 1
  if (SID <= 1) {
    SID = 1;
  }

  // ```` We're going to have a nowTime state variable so that we can keep the page updated when
  // we're displaying the articles from the current date, but I'm going to leave that for last.

  // For convenience and efficiency, create a couple useful Date objects
  const nowDate = new Date()    // Right now
  const todayDate = useMemo(() => {return new Date(new Date().setHours(0,0,0,0))}, []) // Midnight of today

  const fetchBeginningOfWeekTimeStamp = (date) => {
    return date.getTime() - ConfigVars.MillisecondsPerDay * todayDate.getDay()
  }

  // One key state variable is beginWeekTimeStamp, which is initially midnight Sunday of the current
  // week.  The buttons for the week are interpreted relative to this time.
  const [beginWeekTimeStamp, setBeginWeekTimeStamp] =
        useState(fetchBeginningOfWeekTimeStamp(todayDate))

  // Another key state variable is selectedButton, which is initially the button for today.  Buttons
  // are designated as 0-6 for Sunday through Saturday, then 7 for the "Last 24 Hours" button, and 8
  // for the Today button.  The "Last 24 Hours" button only appears when displaying buttons for the
  // current week, while the Today button is only appears when displaying buttons for previous
  // weeks.  The buttons for the week are interpreted relative to beginWeekTimeStamp.
  const [selectedButton, setSelectedButton] = useState(todayDate.getDay())

  // We have to store the information fetched for this Section in a state variable so that it sticks
  // around because the it is fetched from the database and won't be processed until the next
  // rerendering.
  const [Section, setSection] = useState(null)

  // We need to account for changes between Daylight Saving Time and Standard Time, which result in
  // Sunday being either an hour shorter or longer than normal
  let dstAdjustment = 0
  if (new Date(beginWeekTimeStamp).getDay() === new Date(beginWeekTimeStamp + ConfigVars.MillisecondsPerDay).getDay()) {
    // Adding 24 hours to Sunday midnight is still Sunday, so this week switched to Standard Time
    dstAdjustment = ConfigVars.MillisecondsPerHour
  } else if (new Date(beginWeekTimeStamp).getDay() + 1 ===
             new Date(beginWeekTimeStamp + ConfigVars.MillisecondsPerDay - ConfigVars.MillisecondsPerHour).getDay()) {
    // Subtracting one hour from Monday midnight is still Monday, so this week switched to Daylight
    // Saving Time
    dstAdjustment = -ConfigVars.MillisecondsPerHour
  }

  // Fetch the articles for the week specified by beginWeekTimeStamp.  If beginWeekTimeStamp is for
  // this week and if it's Sunday then since the user might click on the "Last 24 Hours" button,
  // fetch back one additional day.
  useEffect(() => {
    console.log('Fetching section data')
    const beginTimeRange = beginWeekTimeStamp === todayDate.getTime()
          ? beginWeekTimeStamp - ConfigVars.MillisecondsPerDay
          : beginWeekTimeStamp
    const endTimeRange = beginWeekTimeStamp + ConfigVars.MillisecondsPerDay * 7 + dstAdjustment - 1
    // Set selected button properly
    if (selectedButton === 8) {
      // Today
      setSelectedButton(todayDate.getDay())
    }
    myFetch('/nt/Section/' + SID + '/' + beginTimeRange + '/' + endTimeRange)
      .then(res => res.json())
      .then(setSection)
  }, [beginWeekTimeStamp, dstAdjustment, selectedButton, todayDate, SID])


  const display24HourButton = beginWeekTimeStamp > todayDate.getTime() - (ConfigVars.MillisecondsPerDay * 7 - 1)

  // Set beginning and end of day taking Daylight Saving Time into account
  const beginningOfDay = (() => {
    switch (selectedButton) {
    case 0:
      // Sunday
      return beginWeekTimeStamp + ConfigVars.MillisecondsPerDay * selectedButton
    default:
      // Monday through Saturday
      return beginWeekTimeStamp + ConfigVars.MillisecondsPerDay * selectedButton + dstAdjustment
    case 7:
      // "Last 24 Hours" button
      return nowDate.getTime() - ConfigVars.MillisecondsPerDay

    case 8:
      // "Today" button
      return todayDate.getTime()
    }
  })()
  const endOfDay = beginningOfDay + ConfigVars.MillisecondsPerDay - 1

  const filterModel = {
    items: [
      {id: 1,
       field: 'Article',
       value: beginningOfDay + ':' + endOfDay,
       operator: 'dayFilter'
      }
    ]
  }

  // Assign the rows for the DataGrid up here so we can memoize them and only assign them when
  // Section changes, othewise they'd get recalculated on every rerender
  const Rows: GridRowsProp = useMemo(() => {
    return Section
      ? Section.Articles.map((Article) => {
        Article.Section = Section
        return (
          {id:            Article.AID,
           Article:       Article,
           PostCount:     Article.PostCount,
           LastPost:      {Comment: Article.LastMsg, Section: Section}
          })})
      : []
  }, [Section])

  // For the date picker, we want the tooltip to disappear after a delay
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [tooltipOpenTimeoutID, setTooltipOpenTimeoutID] = useState(0)
  const [displayTooltipOkay, setDisplayTooltipOkay] = useState(true)
  const [displayTooltipOkayTimeoutID, setDisplayTooltipOkayTimeoutID] = useState(0)

  // We've finished setting the state and have queued up the fetch of data, so if the data isn't
  // ready yet we should return now
  if (!Section) {
    return (
      <Box sx={{fontFamily: 'Roboto', fontSize: 30, textAlign: 'center', margin: 5}}>
        Loading...
      </Box>
    )
  }

  // These two GridFilterModel functions that follow are the way that I would prefer to perform
  // filtering of the rows according to day, but MUI only allows one criterion at a time unless you
  // upgrade to DataGridPro, even though in this case we're referring to the same field (we're just
  // doing a lowbound < x < highbound).  One criterion at a time means that the array size for
  // "items" can't be greater than 1.  I leave these functions here in case I do eventually upgrade to
  // DataGridPro, but I'm not using them for the time being.

  /*
  const todayFilterModel: GridFilterModel = {
    items: [
      { id: 1, field: 'PublishedTime', operator: '>',  value: MidnighTimeStamps[0] }
    ]
  }
  const yesterdayFilterModel: GridFilterModel = {
    items: [
      { id: 1, field: 'PublishedTime', operator: '>', value: MidnighTimeStamps[1] },
      { id: 2, field: 'PublishedTime', operator: '<=', value: MidnighTimeStamps[0] },
    ]
  }
  */

  const dayFilterOperator: GridFilterOperator = {
    label: 'Past Day',
    value: 'dayFilter',
    getApplyFilterFn: (filterItem: GridFilterItem, column: GridColDef) => {
      if (!filterItem.field || !filterItem.value || !filterItem.operator) {
        return null;
      }
      const Values = filterItem.value.split(':')
      return (params: GridCellParams): boolean => {
        const value = Number(params.value.Time)
        // Be exclusive at upper bound except when it's equal to nowDate.
        const lowBound = Number(Values[0])
        const highBound = Number(Values[1])
        if (highBound === nowDate.getTime()) {
          return lowBound <= value && value <= highBound
        } else {
          return lowBound <= value && value < highBound
        }
      }
    }
  }

  const dayOperators = [
    dayFilterOperator
  ]

  const theme = createTheme({
    palette: {
      secondary: {
        main: blue[500]
      }
    }
  })

  // You used to be able to hide columns with the "hide: true" property, but that became deprecated
  // and has now apparently been dropped.  Using columnVisibilityModel now.
  const Columns: GridColDef[] = [
    {field: 'id'},
    {field: 'Article', headerName: 'Discussion Threads', flex: 50, type: 'string', filterOperators: dayOperators, filterVisibility: false,
     renderCell: (params) => (<SectionArticleItem Article={params.value}/>),
     sortComparator: (v1,v2) => v1.Time-v2.Time, sortingOrder: ['desc', 'asc', null]},
    {field: 'PostCount', headerName: 'Number Comments', flex: 16, align: 'center'},
    {field: 'LastPost',  headerName: 'Most Recent Comment', flex: 34,
     renderCell: (params) => (<MostRecentCommentItem props={params.value}/>),
     sortComparator: (v1, v2) => v1.Name.localeCompare(v2.Name)},
  ]

  const getButtonName = (timeStamp) => {
    const theDate = new Date(timeStamp)
    return ((new Date(nowDate.getTime())).setHours(0,0,0,0) === timeStamp)
      ? 'Today'
      : theDate.toLocaleDateString('en-US', {weekday: 'short'}) + ' ' + (theDate.getMonth()+1) + '/' + theDate.getDate()
  }

  const handleWeekButtonClick = (numWeeks) => {
    // Add an hour to handle weeks that include an extra hour plus one millisecond because of
    // daylight savings time
    const extraHourTimeStamp = beginWeekTimeStamp + numWeeks * ConfigVars.MillisecondsPerWeek + ConfigVars.MillisecondsPerHour + 1
    // Now roll back to the actual midnight
    const newBeginWeekTimeStamp = (new Date((new Date(extraHourTimeStamp)).setHours(0,0,0,0))).getTime()
    if (newBeginWeekTimeStamp > nowDate.getTime()) {
      // We've somehow advanced into the future, so do nothing
      return
    }
    setBeginWeekTimeStamp(newBeginWeekTimeStamp)
    setSelectedButton(0)
  }

  const handleDatePickerClick = (newDate) => {
    setBeginWeekTimeStamp(newDate.getTime() - ConfigVars.MillisecondsPerDay * newDate.getDay())
    setSelectedButton(newDate.getDay())
  }

  // Create midnight timestamps for each day of the week
  const MidnightTimeStamps = [
    beginWeekTimeStamp + ConfigVars.MillisecondsPerDay * 0,
    beginWeekTimeStamp + ConfigVars.MillisecondsPerDay * 1 + dstAdjustment,
    beginWeekTimeStamp + ConfigVars.MillisecondsPerDay * 2 + dstAdjustment,
    beginWeekTimeStamp + ConfigVars.MillisecondsPerDay * 3 + dstAdjustment,
    beginWeekTimeStamp + ConfigVars.MillisecondsPerDay * 4 + dstAdjustment,
    beginWeekTimeStamp + ConfigVars.MillisecondsPerDay * 5 + dstAdjustment,
    beginWeekTimeStamp + ConfigVars.MillisecondsPerDay * 6 + dstAdjustment
  ]

  return (
    <>
      <SectionHeader Section={Section}/>
      <ThemeProvider theme={theme}>
        <Box textAlign='center'>
          <Stack direction='row' spacing={2} justifyContent='center'>
            <ButtonGroup size='small' spacing={3} sx={{height: '1.7rem', marginBottom: '7px'}}>
              <Button variant='contained' color={selectedButton === 0 ? 'primary' : 'secondary'}
                      disabled={MidnightTimeStamps[0] > nowDate.getTime()}
                      onClick={() => setSelectedButton(0)}>
                {getButtonName(MidnightTimeStamps[0])}
              </Button>
              <Button variant='contained' color={selectedButton === 1 ? 'primary' : 'secondary'}
                      disabled={MidnightTimeStamps[1] > nowDate.getTime()}
                      onClick={() => setSelectedButton(1)}>
                {getButtonName(MidnightTimeStamps[1])}
              </Button>
              <Button variant='contained' color={selectedButton === 2 ? 'primary' : 'secondary'}
                      disabled={MidnightTimeStamps[2] > nowDate.getTime()}
                      onClick={() => setSelectedButton(2)}>
                {getButtonName(MidnightTimeStamps[2])}
              </Button>
              <Button variant='contained' color={selectedButton === 3 ? 'primary' : 'secondary'}
                      disabled={MidnightTimeStamps[3] > nowDate.getTime()}
                      onClick={() => setSelectedButton(3)}>
                {getButtonName(MidnightTimeStamps[3])}
              </Button>
              <Button variant='contained' color={selectedButton === 4 ? 'primary' : 'secondary'}
                      disabled={MidnightTimeStamps[4] > nowDate.getTime()}
                      onClick={() => setSelectedButton(4)}>
                {getButtonName(MidnightTimeStamps[4])}
              </Button>
              <Button variant='contained' color={selectedButton === 5 ? 'primary' : 'secondary'}
                      disabled={MidnightTimeStamps[5] > nowDate.getTime()}
                      onClick={() => setSelectedButton(5)}>
                {getButtonName(MidnightTimeStamps[5])}
              </Button>
              <Button variant='contained' color={selectedButton === 6 ? 'primary' : 'secondary'}
                      disabled={MidnightTimeStamps[6] > nowDate.getTime()}
                      onClick={() => setSelectedButton(6)}>
                {getButtonName(MidnightTimeStamps[6])}
              </Button>
            </ButtonGroup>
            {display24HourButton
             ? <Button variant='contained' size='small' color={selectedButton === 7 ? 'primary' : 'secondary'}
                       onClick={() => setSelectedButton(7)} sx={{height: '1.7rem', marginBottom: '7px'}}>
                 Last 24 Hours
               </Button>
             : <Button variant='contained' size='small' color='secondary'
                       onClick={() => {setSelectedButton(8); setBeginWeekTimeStamp(fetchBeginningOfWeekTimeStamp(todayDate))}}
                       sx={{height: '1.7rem', marginBottom: '7px'}}>
                 Today
               </Button>
            }
          </Stack>
          <Stack direction='row' spacing={2} justifyContent='center'>
            <Button size='small'onClick={() => handleWeekButtonClick(-1)}>
              {'< Previous Week'}
            </Button>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoItem>
                <Tooltip title='Calendar Date Picker' placement='top-end' open={tooltipOpen}
                         onOpen={
                           () => {
                             setTooltipOpen(displayTooltipOkay && true)
                             if (tooltipOpenTimeoutID === 0) {
                               setTooltipOpenTimeoutID(setTimeout(() => {
                                 setTooltipOpen(false)
                                 setDisplayTooltipOkay(false)
                                 setTooltipOpenTimeoutID(0)
                               }, 5000))
                             }
                             if (displayTooltipOkayTimeoutID === 0) {
                               setDisplayTooltipOkayTimeoutID(setTimeout(() => {
                                 setDisplayTooltipOkay(true)
                                 setDisplayTooltipOkayTimeoutID(0)
                               }, 60000))
                             }
                           }
                         }
                         onClose={() => {clearTimeout(tooltipOpenTimeoutID)
                                         setTooltipOpenTimeoutID(0)
                                         setTooltipOpen(false)}}
                >
              <div>
                <DatePicker value={dayjs(MidnightTimeStamps[selectedButton])} format='M D YYYY'
                            onChange={(newValue) => handleDatePickerClick(new Date(newValue.valueOf()))}
                            sx={{width: '10rem', marginLeft: 'auto', marginRight: 'auto',
                                 '& .MuiOutlinedInput-notchedOutline': {borderStyle: 'none'},
                                 '& .css-nxo287-MuiInputBase-input-MuiOutlinedInput-input': {padding: '3px 0px 5px 0px'},
                                 '& .MuiInputBase-input-MuiOutlinedInput-input': {display: 'none', padding: '3px 0px 5px 0px'}}}/>
            </div>
            </Tooltip>
              </DemoItem>
            </LocalizationProvider>
            <Button size='small'onClick={() => handleWeekButtonClick(1)}
                    disabled={MidnightTimeStamps[6] + ConfigVars.MillisecondsPerDay > nowDate.getTime()}>
              {'Following Week >'}
            </Button>
          </Stack>
        </Box>
      </ThemeProvider>
      <DataGrid
        apiRef={apiRef}
        initialState={{pagination: {paginationModel: {pageSize: ConfigVars.ArticlesPerPage}}}}
        toolbarContainer={{align: 'center'}}
        rows={Rows} columns={Columns} autoHeight={true} hideFooter={false}
        getRowHeight={() => {return 'auto'}}
        slots={{pagination: PageListPagination,
                toolbar: PageListPaginationRightAlign}}
        filterModel={filterModel}
        columnVisibilityModel={{id: false}}
        sx={{'& .MuiDataGrid-filterIcon': {display: 'none'}}}
      />
    </>
  )
}

const SectionArticleItem = (props) => {
  const Article = props.Article
  Article.threadToolTip  = 'Discussion thread for this article'
  Article.articleToolTip = 'Link to this article at the ' + Article.Section.MediaOutlet.Name
  Article.noCommentsYet = ''
  return (
    <ArticleItem {...Article}/>
  )
}

const MostRecentCommentItem = ({props}) => {
  if (props && props.Comment && props.Comment.MID > 0) {
    return (
      <Stack sx={{width: '100%'}}>
        <OffGridCommentLink Comment={props.Comment} Section={props.Section}/>
        <Box>
          by {props.Comment.AuthorName} at <FormattedDateTime TheDate={new Date(props.Comment.Time)} />
        </Box>
      </Stack>
    )
  } else {
    return (
      <>
        No Discussion Yet
      </>
    )
  }
}

const SectionHeader = ({Section}) => {
  return (
    <Box sx={{marginTop: '5px', marginBottom: '15px'}}>
      <Stack direction='row'>
        <MediaOutletImageLink MediaOutlet={Section.MediaOutlet}/>
        <Box align='center' sx={{width: '100%'}}>
          <Table>
            <TableBody>
              <TableListItem
                label='Section List'
                value={<Tooltip title={'List of discussion sections for the ' + Section.MediaOutlet.Name} placement='top'>
                         <Link href={'/MediaOutlet/' + Section.MediaOutlet.MOID}>
                           {Section.MediaOutlet.Name}
                         </Link>
                       </Tooltip>}
              />
              <TableListItem
                label='Section'
                value={<>
                         <Tooltip title={'List of discussion threads for the ' + Section.Name + ' section'} placement='top'>
                           <Link href={'/Section/' + Section.SID}>
                             {Section.Name}
                           </Link>
                         </Tooltip>
                         <Tooltip title={'Link to ' + Section.Name + ' section at the ' + Section.MediaOutlet.Name} placement='top'>
                           <IconButton href={Section.URL}>
                             <NewspaperIcon color='primary'/>
                           </IconButton>
                         </Tooltip>
                       </>
                      }
              />
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </Box>
  )
}
